import { MapViewerNode } from '@3ddv/dvm-internal';
import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Client } from '@shared/models/client.model';
import {
  ApiResponse,
  GeneralAvailabilityDict,
  PatronModel,
  PrivateSellingWhitelistItem,
  SaleEvent,
  SeatAvailabilityDict,
  SeatAvailabilitySectionDict,
  SummaryLinkModel,
  TransactionModel,
  TransactionWrapper,
} from '../models';
import { CartService } from './cart.service';
import { TutorialMocks } from './tutorial-mocks';

@Injectable({ providedIn: 'root' })
export class DemoDataService {
  private readonly router = inject(ActivatedRoute);
  private readonly cartService = inject(CartService);
  tdcIdToMmcId: Record<string, string> = {};

  getFakeResponse(url: string, method: string): any {
    const endpoint = this.extractEndpoint(url);

    switch (endpoint) {
      // Event Landing - Lista de eventos
      case '/events':
        return this.getEventsList();

      // Event Landing - Oponentes
      case '/events/opponents':
        return this.getOpponents();

      // Select Seat - Evento por ID (from API URL)
      case '/events/123':
        return this.getEventById();

      // Select Seat - Evento por ID (direct API call)
      case 'events/123':
        return this.getEventById();

      // Select Seat - Disponibilidad general
      case '/events/123/availability':
        return this.getGeneralAvailability();

      // Select Seat - Disponibilidad multi-sección
      case '/events/123/availability/multi-section':
        return this.getMultiSectionAvailability();

      // Select Seat - Disponibilidad de asientos individuales
      case '/events/123/availability/seats':
        return this.getSeatsAvailability();

      // Select Seat - Crear transacción
      case '/transaction':
        return this.createTransaction();

      // Select Seat - Cliente actual
      case '/clients/current':
        return this.getCurrentClient();

      // Select Seat - Whitelist
      case '/private-selling/test@example.com':
        return this.getPatronWhitelist();

      // Checkout - Transacción por ID
      case '/transaction/123':
        return this.getTransactionById();

      // Payment - Checkout transacción
      case '/transaction/123/checkout':
        return this.checkoutTransaction();

      // Payment - Asociar patron
      case '/transaction/123/add/patron':
        return this.associatePatron();

      // Summary - Summary links
      case '/events/123/summary-links':
        return this.getSummaryLinks();

      // MLB Stats
      case 'mlb-schedule':
        return this.getMlbSchedule();

      default:
        return this.getDefaultResponse();
    }
  }

  isDemoActive(): boolean {
    return this.router.snapshot.queryParams['demo'] === 'true';
  }

  buildGeneralAvailabilityByNodes(
    nodes: MapViewerNode[]
  ): GeneralAvailabilityDict {
    return TutorialMocks.getSectionAvailability(
      nodes.map(node => node.id),
      25
    );
  }

  buildSeatsAvailabilityByNodes(
    selectedSectionId: string,
    sectionSeatNodes: MapViewerNode[]
  ): SeatAvailabilitySectionDict {
    return TutorialMocks.getSeatsAvailability(
      selectedSectionId,
      sectionSeatNodes
    );
  }

  getRandomBestNodes(
    viewerService: MapViewerService,
    seatsAvailability: SeatAvailabilitySectionDict,
    seatsQty: number
  ) {
    const [seats] = Object.values(seatsAvailability);
    const seatIds = new Set(Object.keys(seats));
    const nodes = viewerService.getNodes();
    const randomSeatIds = nodes
      .filter(node => seatIds.has(node.id))
      .sort(() => Math.random() - 0.5)
      .slice(0, seatsQty);
    return randomSeatIds;
  }

  getContiguousBestNodes(
    viewerService: MapViewerService,
    seatsAvailability: SeatAvailabilitySectionDict,
    seatsQty: number
  ) {
    const [seats] = Object.values(seatsAvailability);
    const seatIds = new Set(Object.keys(seats));
    const nodes = viewerService.getNodes();
    const availableSeats = nodes.filter(node => seatIds.has(node.id));

    for (const seat of availableSeats) {
      if (seat.row && Array.isArray(seat.row)) {
        const row = seat.row;
        const seatIndex = row.indexOf(seat.id);

        if (seatIndex !== -1) {
          const consecutiveSeats = [];

          for (let i = 0; i < seatsQty; i++) {
            const targetIndex = seatIndex + i;
            if (targetIndex < row.length) {
              const targetSeatId = row[targetIndex];
              if (targetSeatId) {
                const targetSeat = nodes.find(node => node.id === targetSeatId);

                if (targetSeat && seatIds.has(targetSeatId)) {
                  consecutiveSeats.push(targetSeat);
                } else {
                  break;
                }
              } else {
                break;
              }
            } else {
              break;
            }
          }

          if (consecutiveSeats.length === seatsQty) {
            return consecutiveSeats;
          }

          if (consecutiveSeats.length < seatsQty) {
            const leftConsecutiveSeats = [];
            const startIndex = seatIndex - (seatsQty - 1);

            if (startIndex >= 0) {
              for (let i = 0; i < seatsQty; i++) {
                const targetIndex = startIndex + i;
                const targetSeatId = row[targetIndex];
                if (targetSeatId) {
                  const targetSeat = nodes.find(
                    node => node.id === targetSeatId
                  );

                  if (targetSeat && seatIds.has(targetSeatId)) {
                    leftConsecutiveSeats.push(targetSeat);
                  } else {
                    break;
                  }
                } else {
                  break;
                }
              }

              if (leftConsecutiveSeats.length === seatsQty) {
                return leftConsecutiveSeats;
              }
            }
          }
        }
      }
    }

    console.warn(
      'No consecutive seats found, falling back to random selection'
    );
    return availableSeats.sort(() => Math.random() - 0.5).slice(0, seatsQty);
  }

  setDemoAvailability(viewerService: MapViewerService): void {
    viewerService.getTypesList().forEach(type => {
      if (type === 'general_admission') {
        return;
      }
      const nodesByType = viewerService.getNodesByType(type);
      viewerService.setAvailability(type, nodesByType);
    });
  }

  private extractEndpoint(url: string): string {
    // Handle relative URLs (API calls)
    if (!url.startsWith('http')) {
      // This is a relative URL from the API
      let path = url;

      // Normalizar paths con IDs dinámicos
      path = path.replace(/\/events\/\d+/, '/events/123');
      path = path.replace(/\/transaction\/\d+/, '/transaction/123');
      path = path.replace(
        /\/private-selling\/[^\/]+/,
        '/private-selling/test@example.com'
      );

      // Handle availability endpoints
      if (
        path.includes('/availability') &&
        !path.includes('/multi-section') &&
        !path.includes('/seats')
      ) {
        return '/events/123/availability';
      }
      if (path.includes('/availability/multi-section')) {
        return '/events/123/availability/multi-section';
      }
      if (path.includes('/availability/seats')) {
        return '/events/123/availability/seats';
      }

      return path.replace('/api', '');
    }

    // Handle full URLs
    const urlObj = new URL(url);
    let path = urlObj.pathname;

    // Normalizar paths con IDs dinámicos
    path = path.replace(/\/events\/\d+/, '/events/123');
    path = path.replace(/\/transaction\/\d+/, '/transaction/123');
    path = path.replace(
      /\/private-selling\/[^\/]+/,
      '/private-selling/test@example.com'
    );

    return path.replace('/api', '');
  }

  private getEventsList(): ApiResponse<SaleEvent[]> {
    return {
      message: 'Events retrieved successfully',
      data: [
        {
          id: 1,
          pvEventId: 123,
          code: 'DEMO001',
          description: 'Chicago Cubs vs New York Yankees',
          date: '2024-06-15T19:05:00Z',
          defaultBuyerType: 1,
          defaultBuyerTypeName: 'General Public',
          isEnabled: true,
          adlinkId: null,
          urlImage:
            'https://via.placeholder.com/400x200/1e40af/ffffff?text=Cubs+vs+Yankees',
          extraText: 'Demo Event - Premium Seating Available',
          minTickets: 1,
          maxTickets: 8,
          isPrivateSellingEnabled: false,
          upsells: [
            {
              id: 1,
              active: true,
              name: 'Parking Pass',
              description: 'Premium parking spot near the stadium',
              price: 25.0,
              urlImage:
                'https://via.placeholder.com/200x150/10b981/ffffff?text=Parking',
              buyerTypeId: 1,
              buyerTypeName: 'General Public',
              events: [],
            },
          ],
          upgrades: [
            {
              id: 1,
              active: true,
              name: 'Club Level Upgrade',
              description:
                'Upgrade to club level seating with premium amenities',
              minSeatsToShow: 2,
              goal: 4,
              urlImage:
                'https://via.placeholder.com/200x150/f59e0b/ffffff?text=Club+Level',
              buyerTypeId: 1,
              buyerTypeName: 'General Public',
              price: 150.0,
            },
          ],
        },
        {
          id: 2,
          pvEventId: 124,
          code: 'DEMO002',
          description: 'Chicago Cubs vs Boston Red Sox',
          date: '2024-06-22T13:05:00Z',
          defaultBuyerType: 1,
          defaultBuyerTypeName: 'General Public',
          isEnabled: true,
          adlinkId: null,
          urlImage:
            'https://via.placeholder.com/400x200/dc2626/ffffff?text=Cubs+vs+Red+Sox',
          extraText: 'Demo Event - Family Friendly',
          minTickets: 1,
          maxTickets: 6,
          isPrivateSellingEnabled: false,
        },
      ],
    };
  }

  private getOpponents(): ApiResponse<string[]> {
    return {
      message: 'Opponents retrieved successfully',
      data: [
        'New York Yankees',
        'Boston Red Sox',
        'Los Angeles Dodgers',
        'San Francisco Giants',
      ],
    };
  }

  private getEventById(): ApiResponse<SaleEvent> {
    return {
      message: 'Event retrieved successfully',
      data: {
        id: 1,
        pvEventId: 123,
        code: 'DEMO001',
        description: 'Chicago Cubs vs New York Yankees',
        date: '2024-06-15T19:05:00Z',
        defaultBuyerType: 1,
        defaultBuyerTypeName: 'General Public',
        isEnabled: true,
        adlinkId: null,
        urlImage:
          'https://via.placeholder.com/400x200/1e40af/ffffff?text=Cubs+vs+Yankees',
        extraText: 'Demo Event - Premium Seating Available',
        minTickets: 1,
        maxTickets: 8,
        isPrivateSellingEnabled: false,
        upsells: [
          {
            id: 1,
            active: true,
            name: 'Parking Pass',
            description: 'Premium parking spot near the stadium',
            price: 25.0,
            urlImage:
              'https://via.placeholder.com/200x150/10b981/ffffff?text=Parking',
            buyerTypeId: 1,
            buyerTypeName: 'General Public',
            events: [],
          },
        ],
        upgrades: [
          {
            id: 1,
            active: true,
            name: 'Club Level Upgrade',
            description: 'Upgrade to club level seating with premium amenities',
            minSeatsToShow: 2,
            goal: 4,
            urlImage:
              'https://via.placeholder.com/200x150/f59e0b/ffffff?text=Club+Level',
            buyerTypeId: 1,
            buyerTypeName: 'General Public',
            price: 150.0,
          },
        ],
      },
    };
  }

  private getGeneralAvailability(): ApiResponse<GeneralAvailabilityDict> {
    // Use TutorialMocks to generate realistic section availability
    const sectionIds = ['101', '102', '201', '301'];
    const availableSeatsQuantity = 25;

    const sectionAvailability = TutorialMocks.getSectionAvailability(
      sectionIds,
      availableSeatsQuantity
    );

    return {
      message: 'General availability retrieved successfully',
      data: sectionAvailability,
    };
  }

  private getMultiSectionAvailability(): ApiResponse<SeatAvailabilityDict> {
    // Use TutorialMocks to generate realistic seat availability
    const selectedSectionId = '101';
    const mockSeatNodes = [
      { id: '101-1', original_id: '101-1' },
      { id: '101-2', original_id: '101-2' },
      { id: '101-3', original_id: '101-3' },
      { id: '101-4', original_id: '101-4' },
      { id: '101-5', original_id: '101-5' },
    ];

    const seatsAvailabilitySection = TutorialMocks.getSeatsAvailability(
      selectedSectionId,
      mockSeatNodes as any
    );

    // Extract the seats for the selected section
    const seatsAvailability = seatsAvailabilitySection[selectedSectionId] || {};

    return {
      message: 'Multi-section availability retrieved successfully',
      data: seatsAvailability,
    };
  }

  private getSeatsAvailability(): ApiResponse<SeatAvailabilityDict> {
    // Use TutorialMocks to generate realistic seat availability for individual seats
    const selectedSectionId = '101';
    const mockSeatNodes = [
      { id: '101-1', original_id: '101-1' },
      { id: '101-2', original_id: '101-2' },
      { id: '101-3', original_id: '101-3' },
      { id: '101-4', original_id: '101-4' },
      { id: '101-5', original_id: '101-5' },
      { id: '101-6', original_id: '101-6' },
      { id: '101-7', original_id: '101-7' },
      { id: '101-8', original_id: '101-8' },
    ];

    const seatsAvailabilitySection = TutorialMocks.getSeatsAvailability(
      selectedSectionId,
      mockSeatNodes as any
    );

    // Extract the seats for the selected section
    const seatsAvailability = seatsAvailabilitySection[selectedSectionId] || {};

    return {
      message: 'Seats availability retrieved successfully',
      data: seatsAvailability,
    };
  }

  private createTransaction(): ApiResponse<TransactionModel> {
    // Get selected seats from cart service if available
    const selectedSeats = this.getSelectedSeatsFromCart();
    const seats = selectedSeats.length > 0 ? selectedSeats : ['101-1', '101-2'];

    // Generate seatsPrices based on selected seats
    const seatsPrices = this.generateSeatsPrices(seats);

    return {
      message: 'Transaction created successfully',
      data: {
        id: 123,
        status: 'PH' as const,
        seats: seats,
        originalBuyerTypeId: 1,
        buyerTypeId: 1,
        buyerTypeName: 'General Public',
        originalBuyerTypeName: 'General Public',
        patronId: null,
        eventId: 123,
        hasUnreadErrors: false,
        seatsPrices: seatsPrices,
        token: 'demo-token-12345',
        secretKey: 'demo-secret-key',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  }

  private getCurrentClient(): ApiResponse<Client> {
    return {
      message: 'Current client retrieved successfully',
      data: {
        id: 1,
        isTicketFeeEnabled: false,
        isFeeFromPv: false,
      },
    };
  }

  private getPatronWhitelist(): ApiResponse<PrivateSellingWhitelistItem> {
    return {
      message: 'Patron whitelist retrieved successfully',
      data: {
        id: 123,
        clientId: 1,
        patronId: 123,
        tagId: '123',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  }

  private getTransactionById(): ApiResponse<TransactionWrapper> {
    // Get selected seats from cart service if available
    const selectedSeats = this.getSelectedSeatsFromCart();
    const seats = selectedSeats.length > 0 ? selectedSeats : ['101-1', '101-2'];
    // Generate seatsPrices based on selected seats
    const seatsPrices = this.generateSeatsPrices(seats);
    const totalPriceSeats = Object.values(seatsPrices).reduce(
      (acc, seat) => acc + seat.value,
      0
    );
    const tdcTransaction = {
      id: 456,
      balance: totalPriceSeats,
      serviceCharges: 0,
      cartId: 789,
      cartEtag: 'demo-etag',
      tdcTransactionId: 101112,
      creditAvailable: 0,
      totalSalesRevenue: totalPriceSeats,
      receivableReduction: 0,
      cartExpirationSeconds: 1800,
      hasInsuranceRainout: false,
      insuranceRainoutPrice: null,
      originalBalance: totalPriceSeats,
      originalTotalSalesRevenue: totalPriceSeats,
      rainoutOfferId: null,
      totalPriceSeats: totalPriceSeats,
      transactionId: 123,
      eventOfferId: 123,
      serviceChargesDetails: {
        clientServiceCharges: {},
        cartServiceCharges: {},
      },
    };

    return {
      message: 'Transaction retrieved successfully',
      data: {
        btToken: 'demo-braintree-token',
        transaction: {
          id: 123,
          status: 'PH' as const,
          seats: seats,
          originalBuyerTypeId: 1,
          buyerTypeId: 1,
          buyerTypeName: 'General Public',
          originalBuyerTypeName: 'General Public',
          patronId: 'patron-123',
          eventId: 123,
          hasUnreadErrors: false,
          seatsPrices: seatsPrices,
          tdcTransaction,
          event: {
            id: 1,
            pvEventId: 123,
            code: 'DEMO001',
            description: 'Chicago Cubs vs New York Yankees',
            date: '2024-06-15T19:05:00Z',
            defaultBuyerType: 1,
            defaultBuyerTypeName: 'General Public',
            isEnabled: true,
            adlinkId: null,
            urlImage:
              'https://via.placeholder.com/400x200/1e40af/ffffff?text=Cubs+vs+Yankees',
            extraText: 'Demo Event - Premium Seating Available',
            minTickets: 1,
            maxTickets: 8,
            isPrivateSellingEnabled: false,
          },
          token: 'demo-token-12345',
          secretKey: 'demo-secret-key',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      },
    };
  }

  private checkoutTransaction(): ApiResponse<TransactionModel> {
    // Get selected seats from cart service if available
    const selectedSeats = this.getSelectedSeatsFromCart();
    const seats = selectedSeats.length > 0 ? selectedSeats : ['101-1', '101-2'];

    // Generate seatsPrices based on selected seats
    const seatsPrices = this.generateSeatsPrices(seats);
    return {
      message: 'Transaction checked out successfully',
      data: {
        id: 123,
        status: 'OK' as const,
        seats: seats,
        originalBuyerTypeId: 1,
        buyerTypeId: 1,
        buyerTypeName: 'General Public',
        originalBuyerTypeName: 'General Public',
        patronId: 'patron-123',
        eventId: 123,
        hasUnreadErrors: false,
        seatsPrices: seatsPrices,
        token: 'demo-token-12345',
        secretKey: 'demo-secret-key',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  }

  private associatePatron(): ApiResponse<PatronModel> {
    return {
      message: 'Patron associated successfully',
      data: {
        firstName: 'Demo',
        lastName: 'Patron',
        email: 'test@example.com',
        accountCredit: 100_000,
        dpuPatronAccountId: 123,
        patronProvenueId: 123,
        accountName: 'Demo Patron',
        address: null,
        phone: null,
        digitalWalletList: [],
      },
    };
  }

  private getSummaryLinks(): ApiResponse<SummaryLinkModel[]> {
    return {
      message: 'Summary links retrieved successfully',
      data: [
        {
          id: 1,
          active: true,
          name: 'Demo Summary Link',
          title: 'Demo Summary Link',
          link: 'https://demo.example.com/summary/123',
          urlImage:
            'https://img.mlbstatic.com/mlb-images/image/upload/t_w1536/mlb/dpbqsxequlcrmbkytq5w.jpg?Signature=VuRrnZdfUHpYV0Xw-1NxLakTJJ-MC2EY69Zw4hMSlB7tMz9Uyc8WX3rIZgFqFyeTWNCHclIF55mko1OSnpm2EyGCzpWqJt6hiik6H0eXE4DxOmSZZ58iBfZfbw7A7nKbuIo1teQu1e23yv5WWXpBW48g0ZE~RMwrrqhp-GUe4ZmDhxr3sLT-EBAkiU7YgbAhCH9kNFiqsfcbM55C~Re~laDgcB5v9HiKPm9zLBmdxVAVIjXUHyHsdQT~HaqX2rmI~cSLt8h79Q1Tb7C-mFxH3IEOQJA00KZGQZSn-IZSWc8vCHr2DyqNx5mV3M36O0uDSKOmm03U2rCG6DzJbdxyEQ__&Key-Pair-Id=K2GLXVIC6BHFYE&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9kZXYtZ3JvdXBzYWxlcy5zdGF0aWNyZXNvdXJjZXMuM2RkaWdpdGFsdmVudWUuY29tL2h0dHBzOi8vaW1nLm1sYnN0YXRpYy5jb20vbWxiLWltYWdlcy9pbWFnZS91cGxvYWQvdF93MTUzNi9tbGIvZHBicXN4ZXF1bGNybWJreXRxNXcuanBnIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzgwMDE1NTcyfX19XX0_',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: 2,
          active: true,
          name: 'Demo Summary Link 2',
          title: 'Demo Summary Link 2',
          link: 'https://demo.example.com/summary/123',
          urlImage:
            'https://img.mlbstatic.com/mlb-images/image/upload/t_w1536/mlb/dpbqsxequlcrmbkytq5w.jpg?Signature=VuRrnZdfUHpYV0Xw-1NxLakTJJ-MC2EY69Zw4hMSlB7tMz9Uyc8WX3rIZgFqFyeTWNCHclIF55mko1OSnpm2EyGCzpWqJt6hiik6H0eXE4DxOmSZZ58iBfZfbw7A7nKbuIo1teQu1e23yv5WWXpBW48g0ZE~RMwrrqhp-GUe4ZmDhxr3sLT-EBAkiU7YgbAhCH9kNFiqsfcbM55C~Re~laDgcB5v9HiKPm9zLBmdxVAVIjXUHyHsdQT~HaqX2rmI~cSLt8h79Q1Tb7C-mFxH3IEOQJA00KZGQZSn-IZSWc8vCHr2DyqNx5mV3M36O0uDSKOmm03U2rCG6DzJbdxyEQ__&Key-Pair-Id=K2GLXVIC6BHFYE&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9kZXYtZ3JvdXBzYWxlcy5zdGF0aWNyZXNvdXJjZXMuM2RkaWdpdGFsdmVudWUuY29tL2h0dHBzOi8vaW1nLm1sYnN0YXRpYy5jb20vbWxiLWltYWdlcy9pbWFnZS91cGxvYWQvdF93MTUzNi9tbGIvZHBicXN4ZXF1bGNybWJreXRxNXcuanBnIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzgwMDE1NTcyfX19XX0_',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ],
    };
  }

  private getMlbSchedule(): any {
    return {
      dates: [
        {
          date: '2024-06-15',
          games: [
            {
              gamePk: 12345,
              teams: {
                away: { team: { name: 'New York Yankees' } },
                home: { team: { name: 'Chicago Cubs' } },
              },
              gameDate: '2024-06-15T19:05:00Z',
            },
          ],
        },
      ],
    };
  }

  private getDefaultResponse(): ApiResponse<any> {
    return {
      message: 'Demo response',
      data: { demo: true },
    };
  }

  private getSelectedSeatsFromCart(): string[] {
    const selectedSeats = Object.keys(this.cartService.selectedSeats);
    return selectedSeats;
  }

  private generateSeatsPrices(seats: string[]): Record<string, any> {
    const seatsPrices: Record<string, any> = {};

    seats.forEach(seatId => {
      const seat = this.cartService.selectedSeats[seatId];
      // Use the seat price from cart if available, otherwise use default demo price
      const price = seat?.price || 150.0;

      seatsPrices[seatId] = {
        id: seatId,
        value: price,
        tdcSeatId: `tdc-${seatId}`,
        currencyCode: 'USD',
      };
    });

    return seatsPrices;
  }
}
